<template>
  <GoogleMap
    ref="googleMap"
    :api-key="apiKey"
    :libraries="['places', 'geometry']"
    style="width: 100%; height: 100%;"
    :center="center"
    :zoom="zoom"
    gestureHandling="greedy"
  />
</template>

<script>
import { GoogleMap } from 'vue3-google-map';

export default {
  name: 'RouteEditorMapGoogle',

  components: {
    GoogleMap
  },

  props: {
    apiKey: {
      type: String,
      default: ''
    },
    strategy: {
      type: Object,
      default: null
    },
    waypoints: {
      type: Array,
      default: () => []
    },
    center: {
      type: Object,
      default: () => ({ lat: 45.0732, lng: 7.6827 })
    },
    zoom: {
      type: Number,
      default: 10
    },
    avoidTolls: {
      type: Boolean,
      default: false
    },
    avoidHighways: {
      type: Boolean,
      default: false
    },
    avoidFerries: {
      type: Boolean,
      default: false
    },
    optimizeRoute: {
      type: Boolean,
      default: false
    }
  },

  emits: [
    'ready',
    'route-calculated',
    'route-error',
    'waypoints-optimized'
  ],

  data() {
    return {
      _directionsRenderer: null,
      _mapMarkers: [],
      _isMapReady: false,
      _routeRequestId: 0
    };
  },

  watch: {
    waypoints: {
      deep: true,
      handler() {
        this.refresh();
      }
    },
    avoidTolls() {
      this.refresh();
    },
    avoidHighways() {
      this.refresh();
    },
    avoidFerries() {
      this.refresh();
    }
  },

  mounted() {
    this.$nextTick(() => {
      const map = this.$refs.googleMap?.map;
      if (map) {
        this._isMapReady = true;
        this.initializeDirectionsRenderer();
        this.$emit('ready', map);
      }
    });
  },

  beforeUnmount() {
    this._routeRequestId++;
    this.clearRoute();
    this._directionsRenderer = null;
    this._mapMarkers = [];
  },

  methods: {
    initializeDirectionsRenderer() {
      if (!window.google || !window.google.maps) {
        console.error('Google Maps not loaded');
        return;
      }
      if (this._directionsRenderer) return;

      this._directionsRenderer = new google.maps.DirectionsRenderer({
        suppressMarkers: true,
        preserveViewport: true,
        polylineOptions: {
          strokeColor: '#2563eb',
          strokeWeight: 4,
          strokeOpacity: 0.8
        }
      });

      const map = this.$refs.googleMap?.map;
      if (map) {
        this._directionsRenderer.setMap(map);
      }
    },

    clearRoute() {
      if (this._mapMarkers && this._mapMarkers.length > 0) {
        this._mapMarkers.forEach(m => m.setMap(null));
        this._mapMarkers = [];
      }
      if (this._directionsRenderer) {
        try {
          this._directionsRenderer.setDirections({ routes: [] });
        } catch (e) { }
      }
    },

    drawCustomMarkers(map, route) {
      if (this._mapMarkers && this._mapMarkers.length > 0) {
        this._mapMarkers.forEach(m => m.setMap(null));
        this._mapMarkers = [];
      }

      const startMarker = new google.maps.Marker({
        position: route.legs[0].start_location,
        map: map,
        label: { text: 'P', color: 'white', fontWeight: 'bold' },
        title: 'Partenza'
      });
      this._mapMarkers.push(startMarker);

      route.legs.forEach((leg, i) => {
        if (i < route.legs.length - 1) {
          const marker = new google.maps.Marker({
            position: leg.end_location,
            map: map,
            label: {
              text: (i + 1).toString(),
              color: 'white',
              fontWeight: 'bold'
            },
            title: `Tappa ${i + 1}`
          });
          this._mapMarkers.push(marker);
        }
      });

      const endMarker = new google.maps.Marker({
        position: route.legs[route.legs.length - 1].end_location,
        map: map,
        label: { text: 'A', color: 'white', fontWeight: 'bold' },
        title: 'Arrivo'
      });
      this._mapMarkers.push(endMarker);
    },

    async calculateRoute(preserveView = false, optimize = this.optimizeRoute) {
      if (!this._isMapReady || this.waypoints.length < 2) {
        return;
      }

      if (!this._directionsRenderer) {
        this.initializeDirectionsRenderer();
        if (!this._directionsRenderer) {
          this.$emit('route-error', { message: 'Google Maps non inizializzata' });
          return;
        }
      }

      const requestId = ++this._routeRequestId;
      this.clearRoute();

      try {
        if (!this.strategy?.calculateRoute) {
          throw new Error('Strategia di routing Google non disponibile');
        }

        const result = await this.strategy.calculateRoute({
          waypoints: this.waypoints,
          avoidTolls: this.avoidTolls,
          avoidHighways: this.avoidHighways,
          avoidFerries: this.avoidFerries,
          optimize
        });

        if (requestId !== this._routeRequestId) return;

        this.renderRouteResult(result, preserveView);
        this.$emit('route-calculated', result);

        if (Array.isArray(result.optimizedWaypoints)) {
          this.$emit('waypoints-optimized', result.optimizedWaypoints);
        }
      } catch (error) {
        if (requestId !== this._routeRequestId) return;
        this.$emit('route-error', error);
      }
    },

    renderRouteResult(result, preserveView) {
      const map = this.$refs.googleMap?.map;
      if (!map || !result?.raw || !result?.route || !this._directionsRenderer) {
        throw new Error('Google Maps non ha restituito alcun percorso');
      }

      this.clearRoute();
      this.drawCustomMarkers(map, result.route);
      this._directionsRenderer.setDirections(result.raw);

      if (!preserveView && result.route.bounds) {
        map.fitBounds(result.route.bounds, { padding: 0 });
      }
    },

    emitOptimizedWaypoints(waypointOrder) {
      if (!Array.isArray(waypointOrder) || waypointOrder.length === 0) return;

      const origin = this.waypoints[0];
      const destination = this.waypoints[this.waypoints.length - 1];
      const intermediateWaypoints = this.waypoints.slice(1, -1);

      const reorderedIntermediate = waypointOrder.map(idx => intermediateWaypoints[idx]);

      const optimizedWaypoints = [
        origin,
        ...reorderedIntermediate,
        destination
      ];

      this.$emit('waypoints-optimized', optimizedWaypoints);
    },

    renderSavedPolyline(encodedPolyline) {
      const map = this.$refs.googleMap?.map;
      if (!map || !encodedPolyline || !window.google?.maps?.geometry) {
        return false;
      }

      this.clearRoute();

      try {
        const path = google.maps.geometry.encoding.decodePath(encodedPolyline);
        if (!path || path.length === 0) return false;

        const polyline = new google.maps.Polyline({
          path,
          strokeColor: '#2563eb',
          strokeOpacity: 0.8,
          strokeWeight: 5,
          map,
          zIndex: 5
        });
        this._mapMarkers.push(polyline);

        const startMarker = new google.maps.Marker({
          position: path[0],
          map,
          label: { text: 'P', color: 'white', fontWeight: 'bold' },
          title: 'Partenza'
        });
        const endMarker = new google.maps.Marker({
          position: path[path.length - 1],
          map,
          label: { text: 'A', color: 'white', fontWeight: 'bold' },
          title: 'Arrivo'
        });
        this._mapMarkers.push(startMarker, endMarker);

        this.waypoints.slice(1, -1).forEach((wp, i) => {
          const lat = parseFloat(wp.lat);
          const lng = parseFloat(wp.lng);
          if (!isNaN(lat) && !isNaN(lng)) {
            const marker = new google.maps.Marker({
              position: { lat, lng },
              map,
              label: { text: (i + 1).toString(), color: 'white', fontWeight: 'bold' },
              title: `Tappa ${i + 1}`
            });
            this._mapMarkers.push(marker);
          }
        });

        const bounds = new google.maps.LatLngBounds();
        path.forEach(p => bounds.extend(p));
        map.fitBounds(bounds, { padding: 0 });

        return true;
      } catch (error) {
        console.error('[RouteEditorMapGoogle] renderSavedPolyline error:', error);
        return false;
      }
    },

    refresh() {
      if (!this._isMapReady) return;
      if (this.waypoints.length >= 2) {
        this.calculateRoute(true);
      } else {
        this.clearRoute();
      }
    },

    fitBounds() {
      const map = this.$refs.googleMap?.map;
      if (!map || !this._directionsRenderer) return;

      const directions = this._directionsRenderer.getDirections();
      if (directions && directions.routes.length > 0) {
        map.fitBounds(directions.routes[0].bounds);
      }
    }
  }
};
</script>
